/**
 * User Detail → Sessions → the site-activity half.
 *
 * What this person did on the public science site (zitmus.com). It shares the
 * Sessions tab with the trace list — both answer "what did they do" — but stays
 * its OWN table, because a beacon visit and a session trace are different
 * records: different fields, different retention (180 days here against the
 * trace header's 90) and different provenance. One merged table would leave an
 * operator unable to tell which kind of row they were reading.
 *
 * This is the answer to a question the tab was previously answering wrongly.
 * The science site posts no session traces, so for a visitor who arrived from a
 * campaign and read three simulations, Sessions was empty and the page said
 * "Last active: Never" — while eleven beacon rows sat in `site-events` unread.
 *
 * Grouped by visit, because a visit is the unit that means something: a path
 * through the site. The individual rows are not readable on their own.
 */

import { useQuery } from '@tanstack/react-query';
import { userApi, type SiteVisit } from '../../../api/userApi';

const KIND_LABEL: Record<SiteVisit['events'][number]['kind'], string> = {
  page_view: 'viewed',
  simulation_open: 'ran simulation',
  // History only: the old beacon fired on every load of a bench page.
  lab_open: 'lab page loaded',
  // Recorded by the server as it computes them, in the visit they happened in.
  chemistry_lab_session: 'started lab session',
  chemistry_lab_mix: 'mixed',
};

/** "2 min 14 s", or "—" for a visit with one event, which has no duration. */
function spanOf(visit: SiteVisit): string {
  const ms = new Date(visit.endedAt).getTime() - new Date(visit.startedAt).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const day = (iso: string) => new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' });

export function SiteActivityPanel({ userId }: { userId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['userSiteActivity', userId],
    queryFn: () => userApi.getUserSiteActivity(userId),
    enabled: !!userId,
  });

  if (isLoading) return <div className="loading">Loading site activity…</div>;
  if (error) return <p className="no-features-message">Could not load site activity.</p>;
  if (!data || data.visits.length === 0) {
    return (
      <p className="no-features-message">
        Nothing recorded on zitmus.com in the last {data?.days ?? 30} days.
        Beacons are kept for 180 days.
      </p>
    );
  }

  const { totals } = data;

  return (
    <div className="features-section">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          ['Visits', totals.visits],
          ['Page views', totals.pageViews],
          ['Simulations opened', totals.simulationOpens],
          ['Chemistry lab used', totals.labUses],
          ['Lab mixes', totals.labMixes],
        ].map(([label, value]) => (
          <div key={String(label)} style={{ background: 'var(--bg-tertiary)', padding: 16, borderRadius: 8 }}>
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
          </div>
        ))}
      </div>

      {data.visits.map(visit => (
        <div key={visit.visitId} style={{ marginBottom: 16, border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12,
            padding: '10px 14px', background: 'var(--bg-tertiary)', fontSize: 13,
          }}>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
              {day(visit.startedAt)} {time(visit.startedAt)}
            </span>
            <span style={{ color: 'var(--text-tertiary)' }}>
              {visit.events.length} event{visit.events.length === 1 ? '' : 's'} · {spanOf(visit)}
              {/* Past 14 days the acts themselves are gone and this is the
                  sitting's tally: the same events in the same order, without
                  the minute each happened. */}
              {visit.fromTally ? ' · from summary' : ''}
              {/* Where they came IN from. Self-referrals are the site's own
                  navigation and say nothing about the source. */}
              {visit.referrerHost && visit.referrerHost !== 'zitmus.com' && visit.referrerHost !== 'www.zitmus.com'
                ? ` · from ${visit.referrerHost}`
                : ''}
            </span>
          </div>
          <table className="data-table" style={{ margin: 0 }}>
            <tbody>
              {visit.events.map((e, i) => (
                <tr key={`${e.timestamp}-${i}`}>
                  <td style={{ width: 110, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
                    {visit.fromTally ? '—' : time(e.timestamp)}
                  </td>
                  <td style={{ width: 150, color: 'var(--text-tertiary)' }}>{KIND_LABEL[e.kind] ?? e.kind}</td>
                  <td style={{ fontSize: 13 }}>
                    <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{e.path}</span>
                    {e.detail && <span style={{ color: 'var(--text-secondary)' }}> · {e.detail}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
